import * as React from 'react';
import { observer } from 'mobx-react';
import { NavigationStore } from 'nomst/Stores/Navigation/navigation.store';
import { DecisionDispatcher } from 'nomst/Stores/Decision/decision.dispatcher';
import { Manager } from 'nomst/Stores/Decision/manager';
import { ManagerWorkflowStore } from 'nomst/Stores/Decision/managerWorkflow.store';
import ManagerWorkflow from 'nomst/Components/Decision/managerWorkflow';
import * as decisionUrlBuilder from 'nomst/Stores/Navigation/decisionUrlBuilder';
import { User } from 'nomst/Stores/User/user';
import { CyclePublishedAlert } from 'nomst/Components/Decision/cyclePublishedAlert';
import { CycleClosedAlert } from './cycleClosedAlert';
import { PreviewCycleAlert } from 'nomst/Components/Decision/previewCycleAlert';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { managerListSidePanelStore } from 'utils/MobxDataManager/singletons';
import './managerViewHeader.scss';
import { TeamDecisionStore } from 'nomst/Stores/Decision/teamDecision.store';

const placeholderInitials: string = '--';
const placeholderDisplayName: string = 'XXXXX XXXXXXX';
const placeholderJobTitle: string = 'XXXXXX XXXX XXXXX';

export interface IManagerViewHeaderProps {
    navigationStore: NavigationStore;
    decisionDispatcher: DecisionDispatcher;
    manager: Nullable<Manager>;
    managerWorkflowStore: ManagerWorkflowStore;
    currentUser: User;
    multiManager: boolean;
    managersCount: number;
    firstManagerSourceId: string;
    configuration: Configuration;
    teamDecisionStore: TeamDecisionStore;
}

export class ManagerViewHeader extends React.Component<IManagerViewHeaderProps> {
    constructor(props: IManagerViewHeaderProps) {
        super(props);

        this.goToOrgChart = this.goToOrgChart.bind(this);
        this.exportToCsv = this.exportToCsv.bind(this);
    }

    render(): React.ReactNode {
        const {
            managerWorkflowStore,
            currentUser,
            decisionDispatcher,
            configuration,
            multiManager } = this.props;

        return (
            <div className="manager-header">
                <PreviewCycleAlert />
                <CycleClosedAlert
                    hasHrOrSupportRole={currentUser.isHrOrSupport}
                    configuration={configuration}
                />
                <CyclePublishedAlert
                    hasHrOrSupportRole={currentUser.isHrOrSupport}
                    configuration={configuration}
                    exportAllManagersToCsv={this.exportToCsv}
                />
                <div className="manager-header-content">
                    <div className="flex-row manager-header-content-wrapper">
                        <div className="flex-row left-manager-header-content">
                            {this.renderInitials()}
                            <div>
                                <p className="in-review">Team in Review</p>
                                {this.renderManagerDisplayName()}
                                {this.renderStatusLinks()}
                            </div>
                        </div>
                        <ManagerWorkflow
                            managerWorkflowStore={managerWorkflowStore}
                            currentUser={currentUser}
                            decisionDispatcher={decisionDispatcher}
                            multiManager={multiManager}
                            teamView={this.props.teamDecisionStore.teamView}
                            pdfPrintStatus={this.props.teamDecisionStore.pdfPrintStatus}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private renderInitials(): React.ReactNode {
        const { manager, multiManager, managersCount } = this.props;
        if (!manager || !managerListSidePanelStore.allManagerIds.length) {
            return  (
                <div className="initials placeholder-initials">
                    {placeholderInitials}
                </div>
            );
        }
        if (multiManager) {
            return (
                <div className="initials numerical">
                    {managersCount}
                </div>
            );
        }

        return (
            <div className="initials">
                {manager.initials}
            </div>
        );
    }

    private async exportToCsv(): Promise<void> {
        const { configuration } = this.props;
        const managerDataManager = dataManagerFactory.getManagerDataManager();
        return managerDataManager.exportAllManagersToCsv(
            configuration.masterAccountId, configuration.cycleId);
    }

    private renderManagerDisplayName(): React.ReactNode {
        const { manager, multiManager } = this.props;
        if (!manager || !managerListSidePanelStore.allManagerIds.length) {
            return (
                <h1 className="display-name placeholder-display-name">{placeholderDisplayName}</h1>
            );
        }

        if (multiManager) {
            return (
                <h1 className="display-name">Teams Selected</h1>
            );
        }

        return (
            <h1 className="display-name">{manager.displayName}</h1>
        );
    }

    private renderStatusLinks(): React.ReactNode {
        const { multiManager } = this.props;

        if (multiManager) {
            return (
                <div className="status-links__multi-manager">
                    {this.renderViewOrgLink()}
                </div>
            );
        }
        return (
            <div className="status-links__single-manager">
                {this.renderManagerJobTitle()}
                <span
                    className="link-icon"
                >
                    {this.renderViewOrgLink()}
                </span>
            </div>
        );

    }

    private renderManagerJobTitle(): React.ReactNode {
        const { manager, multiManager } = this.props;
        const className = 'placeholder-job-title';
        if (!manager || !managerListSidePanelStore.allManagerIds.length) {
            return (
                <span className={`job-title ${className}`}>{placeholderJobTitle}</span>
            );
        }

        if (multiManager) {
            return null;
        }

        return (
            <span className="job-title">{manager.jobTitleInternal}</span>
        );
    }

    private renderViewOrgLink(): React.ReactNode {
        const { manager } = this.props;
        if (!manager || !managerListSidePanelStore.allManagerIds.length) {
            return (
                <span className="view-org placeholder-view-org">View Org</span>
            );
        }

        return (
            <a
                href={decisionUrlBuilder.getOrgChartRoute(manager.employeeSourceId)}
                onClick={this.goToOrgChart}
            >
                View Org
            </a>
        );
    }

    private goToOrgChart(clickEvent: React.MouseEvent<HTMLAnchorElement>): void {
        const { manager } = this.props;
        const { navigationStore } = this.props;
        navigationStore.navigateToOrgChartPage(manager?.employeeSourceId ?? '');

        clickEvent.preventDefault();
    }
}

export default observer(ManagerViewHeader);